import React, { useState } from 'react';
import { Button, Table, Row } from 'react-bootstrap';
import KaAlert from '../Common/KaAlert'
import { useUser } from '../UserAccess/useUser';
import useDisplayMessage from '../Common/useDisplayMessage';
import useUserRequest, { UserRequestSpec } from '../Common/useUserRequest';
import useGetUsers, { BusinessUserDto, BusinessUserUpsertDto } from '../Requests/useGetUsers';
import { useSelectedBusiness } from '../UserAccess/useSelectedBusiness';
import { AddUser } from './AddUser';
import { EditUser } from './EditUser';
import BusinessAdminTemplate from '../UserAccess/BusinessAdminTemplate';
import Confirm from '../Common/Confirm';
import { HttpMethod } from '../Common/useFetch';
import BoldButton from '../Common/BoldButton';
import QueryView from '../Common/QueryView';
import FilterInput, { textSatisfiesFilter } from '../FilterInput';
import OverflowTooltip from '../Common/OverflowTooltip';

const removeSpec = (businessId?: string, userId?: string): UserRequestSpec => ({
  path: `${baseUserUrl(businessId, userId)}`,
  method: HttpMethod.DELETE,
});

const baseUserUrl = (businessId?: string, userId?: string) => `${baseUrl(businessId)}/${userId}`;

const baseUrl = (businessId?: string) => `/api/businesses/${businessId}/users`;

const UserPage = () => {
  const currentUserId = useUser().id;
  const business = useSelectedBusiness();
  const displayMessage = useDisplayMessage();
  const getUsers = useGetUsers({ autoQuery: business.administrator });
  const [user, setUser] = useState<BusinessUserDto | null>(null);
  const [filterText, setFilterText] = useState('');

  const [addUserVisible, setAddUserVisible] = useState(false);

  const addUserSuccess = (u: BusinessUserUpsertDto) => {
    displayMessage.success(`Added ${u.emailAddress} to business: ${business.name}`);
    getUsers.query();
    setAddUserVisible(false);
  }

  const addClicked = () => setAddUserVisible(true);

  const addUserDismissed = () => setAddUserVisible(false);

  const [editUserVisible, setEditUserVisible] = useState(false);

  const editClicked = (businessUser: BusinessUserDto) => {
    setUser(businessUser);
    setEditUserVisible(true);
  };

  const editUserDismissed = () => setEditUserVisible(false);

  const editSuccess = () => {
    displayMessage.success(`${user?.emailAddress} updated.`);
    getUsers.query();
    setEditUserVisible(false);
  };

  const [confirmRemoveVisible, setConfirmRemoveVisible] = useState(false);

  const remove = useUserRequest(
    removeSpec(business.id, user?.id), {
      onComplete: () => setConfirmRemoveVisible(false),
      onSuccess: () => {
        displayMessage.success(`${user?.emailAddress} removed.`);
        getUsers.query();
      },
      onError: (err) => displayMessage.fail(err.message)
    });

  const removeClicked = (businessUser: BusinessUserDto) => {
    setUser(businessUser);
    setConfirmRemoveVisible(true);
  };

  const confirmRemoveDismissed = () => setConfirmRemoveVisible(false);

  const removalConfirmed = () => remove.request(null);

  return (<BusinessAdminTemplate>
    <QueryView
        query={getUsers}
        renderData={({ userRoles, users }) => (<>
          <AddUser
            visible={addUserVisible}
            onSuccess={addUserSuccess}
            roles={userRoles}
            onHide={addUserDismissed} />
          {user && <EditUser
            key={user.id}
            visible={editUserVisible}
            businessUser={user}
            roles={userRoles}
            onHide={editUserDismissed}
            onSuccess={editSuccess} />}
          <Confirm
            visible={confirmRemoveVisible}
            title='Delete User'
            body={<>
              Are you sure that you want to permanently remove
              <span className="d-block text-truncate fw-bold" title={user?.emailAddress}>{user?.emailAddress}</span>
            </>}
            isLoading={remove.isLoading}
            onDismiss={confirmRemoveDismissed}
            onConfirm={removalConfirmed} />
          <Row className="mb-2">
            <h2 className="col-auto ka-blue">Users & Profiles</h2>
          </Row>
          <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
          <Row className="g-3 mb-4">
            {[...userRoles].sort((a, b) => Number(b.administrator) - Number(a.administrator)).map(role => {
              const userCount = users.filter(u => u.userRoleId === role.id).length;
              return (
                <div className="col-md-4" key={role.id}>
                  <div className="card h-100 border border-2">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between gap-2">
                        <div className="overflow-hidden">
                          <div className="d-flex align-items-center gap-2">
                            <span className="avatar avatar-sm avatar-circle avatar-soft-primary p-0 flex-shrink-0">
                              <span className="avatar-initials">{role.name.charAt(0).toUpperCase()}</span>
                            </span>
                            <h5 className="card-title mb-0 text-truncate" title={role.name}>{role.name}</h5>
                          </div>
                          <OverflowTooltip className="card-text text-muted small mt-2 mb-0" text={role.description} lines="2" />
                        </div>
                        <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
                          <span className="badge bg-soft-primary text-primary">{userCount} {userCount === 1 ? 'user' : 'users'}</span>
                          <div className="mt-4 ms-1 me-2">
                            {/* Edit/Delete profile actions are hidden until they are implemented
                            <BoldButton className="btn-ghost-secondary btn-sm p-1" title="Edit profile"><i className="bi bi-pencil-square fs-3 px-1" /></BoldButton>
                            <BoldButton className="btn-ghost-danger btn-sm p-1" title="Delete profile"><i className="bi bi-trash fs-3 px-1" /></BoldButton>
                            */}
                            <i className="bi bi-lock-fill text-muted fs-3" title="Locked" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>);
            })}
          </Row>
          <hr className="my-4" />
          <Row className="justify-content-between align-items-center mb-2">
            <div className="col-md-4">
              <FilterInput
                placeholder="Search users..."
                value={filterText}
                size="md"
                onFilterTextChange={setFilterText} />
            </div>
            <BoldButton className="col-auto btn-ghost-primary me-4" onClick={addClicked}>Add User</BoldButton>
          </Row>
          <Table borderless>
            <thead>
              <tr className="border-bottom border-dark border-1">
                <th className="col-6">User</th>
                <th className="col-5">Access</th>
                <th className="col-1"></th>
              </tr>
            </thead>
            <tbody>
              {users.filter(u => textSatisfiesFilter(filterText)(u.emailAddress)).map(businessUser =>
                <tr key={businessUser.id} className="border-top">
                  <td className="py-2 align-middle" style={{ maxWidth: 0 }}>
                    <div className="d-flex align-items-center gap-2">
                      <span className="avatar avatar-sm avatar-circle avatar-soft-primary p-0 flex-shrink-0">
                        <span className="avatar-initials">{businessUser.emailAddress.charAt(0).toUpperCase()}</span>
                      </span>
                      <span className="text-truncate" title={businessUser.emailAddress}>{businessUser.emailAddress}</span>
                      {!businessUser.hasSignedIn && <span className="badge bg-soft-warning text-dark border border-warning flex-shrink-0">Invite pending</span>}
                    </div>
                  </td>
                  <td className="py-2 align-middle" style={{ maxWidth: 0 }}>
                    <span className="badge bg-soft-primary text-primary text-truncate mw-100" title={userRoles.find(r => r.id === businessUser.userRoleId)?.name}>
                      {userRoles.find(r => r.id === businessUser.userRoleId)?.name}
                    </span>
                  </td>
                  <td className="py-2 align-middle">
                    {businessUser.id !== currentUserId &&
                      <div className="dropdown">
                        <Button className='dropdown-toggle btn-ghost-secondary' variant='link' id='actionsMenuButton' data-bs-toggle='dropdown'>
                          <strong>Actions</strong>
                        </Button>
                        <div className='dropdown-menu'>
                          <BoldButton className='dropdown-item btn-ghost-secondary' onClick={() => editClicked(businessUser)}>Edit User</BoldButton>
                          <BoldButton className='dropdown-item btn-ghost-secondary' onClick={() => removeClicked(businessUser)}>Delete User</BoldButton>
                        </div>
                      </div>}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </>)} />
  </BusinessAdminTemplate>);
}

export { UserPage }
